import { MutationHandlingServiceV2, QueryHandlingService } from '@akebono/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { combineLatest, Observable, of } from 'rxjs';
import { finalize, map, startWith, switchMap, take } from 'rxjs/operators';

import { AutoLotView } from '../../../const';
import {
  CurrentLotMarksListsGQL,
  CurrentLotMarksListsQuery,
  LotSource,
  LotTypeEnum,
  UserLotMarkCreateGQL,
  UserLotMarkEnum,
  UserLotMarksListCreateGQL,
} from '../../graphql/service/graphql-auto-main-service';

@Component({
  selector: 'app-lot-add-shared-list-modal-button',
  templateUrl: './lot-add-shared-list-modal-button.component.html',
  styleUrls: ['./lot-add-shared-list-modal-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotAddSharedListModalButtonComponent implements OnInit {
  @Input() lot: AutoLotView;
  @Input() lotType: LotTypeEnum;
  @Input() lotSource: LotSource;
  @Input() fullSize = false;
  @Input() isUpdate = false;
  @Output() actionCompleted = new EventEmitter<void>();

  markCreating$ = of(false);
  listCreating$ = of(false);
  currentLotMarksLists$: Observable<CurrentLotMarksListsQuery['currentUser']['lotMarksLists']>;

  constructor(
    private qhs: QueryHandlingService,
    private mhs: MutationHandlingServiceV2,
    private readonly translate: TranslateService,
    private readonly modalService: NzModalService,
    private currentLotMarksListsGQL: CurrentLotMarksListsGQL,
    private userLotMarksListCreateGQL: UserLotMarksListCreateGQL,
    private userLotMarkCreateGQL: UserLotMarkCreateGQL,
  ) {}

  ngOnInit(): void {
    this.currentLotMarksLists$ = this.qhs
      .watch(this.currentLotMarksListsGQL, {})
      .data.pipe(map((data) => data?.currentUser?.lotMarksLists ?? []));
  }

  private userLotMarkCreate(userLotMarksListId: string): Observable<boolean> {
    return this.mhs.mutate(
      this.userLotMarkCreateGQL,
      {
        input: {
          lotId: this.lot.id,
          lotType: this.lotType,
          lotSource: this.lotSource,
          mark: UserLotMarkEnum.Shared,
          userLotMarksListId,
        },
      },
      {
        refetch: true,
        successTranslationKey: 'MUTATION_SUCCEEDED',
        failureTranslationKey: 'MUTATION_FAILED',
      },
    ).loading;
  }

  executeUserLotMarkCreate(userLotMarksListId: string): void {
    this.markCreating$ = this.userLotMarkCreate(userLotMarksListId).pipe(
      finalize(() => this.actionCompleted.emit()),
    );
  }

  async userLotMarksListCreate(): Promise<void> {
    const collectionTitle = this.translate.instant('SHARED.ADD_NEW_LISTS');

    const lotMarksLists = await this.currentLotMarksLists$.pipe(take(1)).toPromise();
    const titles = lotMarksLists.map((list) => list.title);

    const indexes = titles
      .filter((title) => title.startsWith(collectionTitle))
      .map((title) => {
        const match = title.match(/\d+$/);
        return match ? parseInt(match[0], 10) : 0;
      });

    const newIndex = indexes.length ? Math.max(...indexes) + 1 : 1;
    const newTitle = indexes.length ? `${collectionTitle} ${newIndex}` : collectionTitle;

    const listCreateRef = this.mhs.mutate(
      this.userLotMarksListCreateGQL,
      {
        input: {
          title: newTitle,
          publishedAt: new Date().toISOString(),
        },
      },
      {
        refetch: true,
        renderSuccess: false,
        failureTranslationKey: 'MUTATION_FAILED',
      },
    );
    const markCreating$ = listCreateRef.data.pipe(
      map((result) => {
        const newListId = result.userLotMarksListCreate?.userLotMarksListId;
        return newListId;
      }),
      switchMap((newListId) => this.userLotMarkCreate(newListId)),
      finalize(() => this.actionCompleted.emit()),
      startWith(true),
    );

    this.listCreating$ = combineLatest([listCreateRef.loading, markCreating$]).pipe(
      map((loadings) => loadings.some((l) => l)),
    );
  }
}
